import { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CallTrackingService } from '../../services/callTrackingService';
import { DateRange } from '../../types/callTracking';
import { format } from 'date-fns';

interface CallMetricsWidgetProps {
  className?: string;
}

export function CallMetricsWidget({ className = '' }: CallMetricsWidgetProps) {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const callTrackingService = new CallTrackingService(apolloClient);
  
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('today');

  useEffect(() => {
    loadMetrics();
  }, [selectedPeriod]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const range = getDateRange();
      const result = await callTrackingService.getCallMetrics(range);
      setMetrics(result);
    } catch (err) {
      console.error('Error loading call metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (): DateRange => {
    const end = new Date().toISOString();
    const now = new Date();
    let start: string;
    
    switch (selectedPeriod) {
      case 'today':
        start = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        break;
      case 'week':
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        start = weekAgo.toISOString();
        break;
      case 'month':
        const monthAgo = new Date(now);
        monthAgo.setDate(monthAgo.getDate() - 30);
        start = monthAgo.toISOString();
        break;
      default:
        start = new Date(now.setHours(0, 0, 0, 0)).toISOString();
    }
    
    return { start, end };
  };

  const getOutcomeStats = () => {
    if (!metrics?.outcomeBreakdown) return [];
    
    const total = metrics.totalCalls || 0;
    const outcomes = [
      { 
        label: 'Answered', 
        count: metrics.outcomeBreakdown.ANSWERED || 0,
        color: 'text-green-700 bg-green-50 border-green-200',
        icon: '✓'
      },
      { 
        label: 'Voicemail', 
        count: metrics.outcomeBreakdown.VOICEMAIL || 0,
        color: 'text-blue-700 bg-blue-50 border-blue-200',
        icon: '◉'
      },
      { 
        label: 'No Answer', 
        count: metrics.outcomeBreakdown.NO_ANSWER || 0,
        color: 'text-gray-700 bg-gray-50 border-gray-200',
        icon: '○'
      },
      { 
        label: 'Busy', 
        count: metrics.outcomeBreakdown.BUSY || 0,
        color: 'text-orange-700 bg-orange-50 border-orange-200',
        icon: '⊗'
      },
    ];

    return outcomes.map(outcome => ({
      ...outcome,
      percentage: total > 0 ? ((outcome.count / total) * 100).toFixed(0) : '0'
    }));
  };

  if (loading) {
    return (
      <Card className={`h-full ${className}`}>
        <CardHeader className="border-b border-gray-100 pb-4">
          <CardTitle className="text-base font-semibold text-aalaw-charcoal flex items-center gap-2">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Call Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const outcomeStats = getOutcomeStats();
  const answeredRate = metrics?.totalCalls > 0 
    ? ((metrics.outcomeBreakdown?.ANSWERED || 0) / metrics.totalCalls * 100).toFixed(0)
    : '0';

  return (
    <Card className={`h-full ${className}`}>
      <CardHeader className="border-b border-gray-100 pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-aalaw-charcoal flex items-center gap-2">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Call Activity
          </CardTitle>
          <div className="flex gap-1">
            {(['today', 'week', 'month'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  selectedPeriod === period
                    ? 'bg-aalaw-red text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {period === 'today' ? 'Today' : period === 'week' ? '7d' : '30d'}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        {/* Primary Metrics */}
        <div className="grid grid-cols-2 gap-4">
          {/* Total Calls */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">Total Calls</span>
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div className="text-3xl font-bold text-blue-900">{metrics?.totalCalls || 0}</div>
          </div>

          {/* Answer Rate */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-900">Answered</span>
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-3xl font-bold text-green-900">{answeredRate}%</div>
          </div>
        </div>

        {/* Average Duration */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-sm text-gray-600">Average Duration</div>
                <div className="text-2xl font-semibold text-gray-900">
                  {callTrackingService.formatDuration(metrics?.averageDuration || 0)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Outcome Breakdown */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Call Outcomes</h4>
          <div className="space-y-2">
            {outcomeStats.map((outcome) => (
              <div key={outcome.label} className={`flex items-center justify-between p-3 rounded-lg border ${outcome.color}`}>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold">{outcome.icon}</span>
                  <span className="text-sm font-medium">{outcome.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">{outcome.count}</span>
                  <span className="text-xs text-gray-600">({outcome.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Staff Performance */}
        {metrics?.staffPerformance && metrics.staffPerformance.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Callers</h4>
            <div className="space-y-2">
              {metrics.staffPerformance.slice(0, 3).map((staff: any, index: number) => (
                <div key={staff.staffId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-aalaw-red text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium text-gray-900">{staff.staffName}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {staff.totalCalls} calls
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View All Link */}
        <Button
          variant="outline"
          className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
          onClick={() => navigate('/call-logs')}
        >
          View All Call Logs
          <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>

        {/* Last Updated */}
        <div className="pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Updated {format(new Date(), 'h:mm a')}</span>
            <button
              onClick={loadMetrics}
              className="text-aalaw-red hover:text-aalaw-red-dark font-medium"
            >
              Refresh
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
